import {
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useState } from "react";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

interface UseGridPersistenceOptions {
  persistenceKey: string;
  initialSortModel: GridSortModel;
  initialPageSize: number;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  isMobile?: boolean;
  /**
   * When false, sort model is kept in component state instead of localStorage,
   * so each mount starts from `initialSortModel`. Other settings still persist.
   * Defaults to true.
   */
  persistSortModel?: boolean;
}

interface LocalStorageData {
  pageSize: number;
  sortModel: GridSortModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnWidthModel: Record<string, number>;
  // Screen-size-aware settings
  mobile?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
  desktop?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
}

/**
 * useGridPersistence manages the data grid settings in local storage
 * with support for separate mobile and desktop settings
 */
export function useGridPersistence({
  persistenceKey,
  initialSortModel,
  initialPageSize,
  initialColumnVisibilityModel = {},
  isMobile = false,
  persistSortModel = true,
}: UseGridPersistenceOptions) {
  const [settings, setSettings] = useLocalStorageState<LocalStorageData>(
    `grid-settings-${persistenceKey}`,
    {
      defaultValue: {
        pageSize: initialPageSize,
        sortModel: initialSortModel,
        columnVisibilityModel: {},
        columnWidthModel: {},
        mobile: {
          columnVisibilityModel: {},
          columnWidthModel: {},
        },
        desktop: {
          columnVisibilityModel: {},
          columnWidthModel: {},
        },
      },
    }
  );

  // Get screen-size-specific settings
  const screenSizeKey = isMobile ? "mobile" : "desktop";
  const currentScreenSettings = useMemo(() => {
    const screenSettings = settings?.[screenSizeKey];
    return {
      columnVisibilityModel: screenSettings?.columnVisibilityModel || {},
      columnWidthModel: screenSettings?.columnWidthModel || {},
    };
  }, [settings, screenSizeKey]);

  // Merge initial column visibility model with persisted settings for current screen size
  // User preferences (persisted) take priority over initial settings
  const mergedColumnVisibilityModel = useMemo(() => {
    const persistedModel = currentScreenSettings.columnVisibilityModel || {};
    const merged = { ...initialColumnVisibilityModel };

    // Apply persisted user preferences on top of initial settings
    // Check if persistedModel exists and is an object before using Object.keys
    if (persistedModel && typeof persistedModel === "object") {
      Object.keys(persistedModel).forEach((field) => {
        merged[field] = persistedModel[field];
      });
    }

    return merged;
  }, [currentScreenSettings.columnVisibilityModel, initialColumnVisibilityModel]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      setSettings((prev) => ({
        ...prev,
        pageSize,
        paginationModel: undefined,
      }));
    },
    [setSettings]
  );

  // Non-persisted sort state, used when persistSortModel is false so the sort
  // resets to initialSortModel on each mount.
  const [transientSortModel, setTransientSortModel] = useState<GridSortModel>(initialSortModel);

  const setSortModel = useCallback(
    (model: GridSortModel) => {
      if (persistSortModel) {
        setSettings((prev) => ({ ...prev, sortModel: model }));
      } else {
        setTransientSortModel(model);
      }
    },
    [persistSortModel, setSettings]
  );

  const resetSortModel = useCallback(() => {
    if (persistSortModel) {
      setSettings((prev) => ({ ...prev, sortModel: initialSortModel }));
    } else {
      setTransientSortModel(initialSortModel);
    }
  }, [initialSortModel, persistSortModel, setSettings]);

  const setColumnVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel) => {
      setSettings((prev) => ({
        ...prev,
        [screenSizeKey]: {
          ...prev?.[screenSizeKey],
          columnVisibilityModel: model,
        },
      }));
    },
    [setSettings, screenSizeKey]
  );

  const setColumnWidthModel = useCallback(
    ({ colDef: { field }, width }: GridColumnResizeParams) => {
      setSettings((prev) => ({
        ...prev,
        [screenSizeKey]: {
          ...prev?.[screenSizeKey],
          columnWidthModel: {
            ...prev?.[screenSizeKey]?.columnWidthModel,
            [field]: width,
          },
          // Ensure columnVisibilityModel is preserved
          columnVisibilityModel: prev?.[screenSizeKey]?.columnVisibilityModel || {},
        },
      }));
    },
    [setSettings, screenSizeKey]
  );

  const unsetColumnWidthModel = useCallback(
    (field: string) => {
      setSettings((prev) => {
        const updated = { ...prev?.[screenSizeKey]?.columnWidthModel };
        delete updated[field];

        return {
          ...prev,
          [screenSizeKey]: {
            ...prev?.[screenSizeKey],
            columnWidthModel: updated,
            // Ensure columnVisibilityModel is preserved
            columnVisibilityModel: prev?.[screenSizeKey]?.columnVisibilityModel || {},
          },
        };
      });
    },
    [setSettings, screenSizeKey]
  );

  const resetColumnSettings = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      [screenSizeKey]: {
        ...prev?.[screenSizeKey],
        columnVisibilityModel: {},
        columnWidthModel: {},
      },
    }));
  }, [setSettings, screenSizeKey]);

  // Stabilize the resolved sortModel reference. MUI DataGrid treats any change to
  // the sortModel reference as a real "sort model change" and resets the page to 0.
  // Writing any grid setting (e.g. a column resize) re-parses the settings blob
  // from localStorage, so `storedSortModel` comes back as a new array with
  // identical contents. Deep-compare memoizing the resolved model keeps the
  // reference stable, so resizing no longer jumps the user back to page 1.
  const storedSortModel = settings?.sortModel;
  let resolvedSortModel: GridSortModel;
  if (!persistSortModel) {
    resolvedSortModel = transientSortModel;
  } else if (storedSortModel?.length) {
    resolvedSortModel = storedSortModel;
  } else {
    resolvedSortModel = initialSortModel;
  }
  const stableSortModel = useDeepCompareMemoize(resolvedSortModel);

  return {
    pageSize: settings?.pageSize ?? initialPageSize,
    sortModel: stableSortModel,
    columnVisibilityModel: mergedColumnVisibilityModel,
    columnWidthModel: currentScreenSettings.columnWidthModel || {},
    setPageSize,
    setSortModel,
    resetSortModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
  };
}
